import { type BooleanInput } from '@angular/cdk/coercion';
import { CdkMenuItemCheckbox } from '@angular/cdk/menu';
import { booleanAttribute, computed, Directive, inject, input, linkedSignal } from '@angular/core';
import { hlm } from '@spartan-ng/helm/utils';
import type { ClassValue } from 'clsx';

@Directive({
  selector: '[hlmDropdownMenuCheckbox]',
  hostDirectives: [
    {
      directive: CdkMenuItemCheckbox,
      inputs: ['cdkMenuItemDisabled: disabled', 'cdkMenuItemChecked: checked'],
      outputs: ['cdkMenuItemTriggered: triggered'],
    },
  ],
  host: {
    'data-slot': 'dropdown-menu-checkbox-item',
    '[attr.data-disabled]': 'disabled() ? "" : null',
    '[attr.data-checked]': '_checkedState() ? "" : null',
    '[attr.data-indeterminate]': 'indeterminate() ? "" : null',
    '[class]': '_computedClass()',
  },
})
export class HlmDropdownMenuCheckbox {
  private readonly _cdkMenuItem = inject(CdkMenuItemCheckbox);

  constructor() {
    // Empêche la fermeture du menu lorsqu'on (dé)coche l'élément : on force keepOpen
    // sur le trigger CDK, qui sinon ferme toute la pile de menus au clic/Enter/Espace.
    // On resynchronise aussi l'état affiché : CdkMenuItemCheckbox bascule son propre
    // champ `checked` interne, qui n'est pas relié au signal de la directive helm.
    const triggerWithMenuOpen = this._cdkMenuItem.trigger.bind(this._cdkMenuItem);
    this._cdkMenuItem.trigger = (options) => {
      triggerWithMenuOpen({ ...options, keepOpen: true });
      this._checkedState.set(this._cdkMenuItem.checked);
    };
  }

  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  protected readonly _computedClass = computed(() =>
    hlm(
      'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground group relative flex w-full cursor-pointer items-center rounded-sm py-[6px] pr-2 pl-xl component-medium-medium transition-colors outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-left',
      this.userClass(),
    ),
  );

  public readonly checked = input<boolean, BooleanInput>(this._cdkMenuItem.checked, { transform: booleanAttribute });
  // État affiché : initialisé depuis l'input `checked`, puis basculé localement au clic.
  protected readonly _checkedState = linkedSignal(() => this.checked());
  public readonly indeterminate = input<boolean, BooleanInput>(false, { transform: booleanAttribute });
  public readonly disabled = input<boolean, BooleanInput>(this._cdkMenuItem.disabled, { transform: booleanAttribute });
}
